import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useProjectsQuery, useMyProjectRolesQuery } from "@/hooks/queries/useProjectsQuery";
import { apiClient } from "@/lib/apiClient";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Calendar
} from "lucide-react";
import { AgentType, AGENT_TYPE_LABELS, AGENT_TYPE_TRANSLATION_KEYS, DOMAIN_AGENT_TYPES, WorkstreamStatus, safeParseJsonArray } from "@/types/workstream";
import type { WorkstreamDto, WorkstreamFinding } from "@/types/workstream";
import type { Project } from "@/types/project";
import { RoleType, ProjectStatus } from "@/types/project";

const RISK_SCORE_SCALE = 10;
const CRITICAL_SEVERITY_RISK_THRESHOLD = 9;
const HIGH_SEVERITY_RISK_THRESHOLD = 7;
const PRINT_DELAY_MS = 250;

const escapeHtml = (value: string | null | undefined): string =>
  (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getDeviationSeverity = (riskScore: number | null, isKeyGap: boolean): "critical" | "high" | "medium" | "low" => {
  if (isKeyGap) return "low";
  if (riskScore !== null && riskScore >= CRITICAL_SEVERITY_RISK_THRESHOLD) return "critical";
  if (riskScore !== null && riskScore >= HIGH_SEVERITY_RISK_THRESHOLD) return "high";
  return "medium";
};

const getExportSeverityClass = (severity: "critical" | "high" | "medium" | "low"): string => {
  switch (severity) {
    case "critical": return "severity-critical";
    case "high": return "severity-high";
    case "medium": return "severity-medium";
    case "low": return "severity-low";
  }
};

const POLICY_DEVIATION_SEVERITY_ORDER: Record<"critical" | "high" | "medium" | "low", number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

type PolicyDeviationEntry = {
  project: string;
  policy: string;
  deviation: string;
  severity: "critical" | "high" | "medium" | "low";
};

type ExportFindingSection = {
  title: string;
  agentType: number;
  riskScore: number | null;
  findingTitles: string[];
  redFlags: string[];
  keyGaps: string[];
};

const buildPolicyDeviationEntries = (
  workstreams: WorkstreamDto[],
  getProjectName: (projectId: number) => string,
  getAgentLabel: (agentType: number) => string,
): PolicyDeviationEntry[] =>
  workstreams
    .flatMap((ws) => {
      const projectName = getProjectName(ws.projectId);
      const policy = getAgentLabel(ws.agentType);
      const redFlags = safeParseJsonArray<string>(ws.redFlags);
      const keyGaps = safeParseJsonArray<string>(ws.keyGaps);

      return [
        ...redFlags.map((deviation) => ({
          project: projectName,
          policy,
          deviation,
          severity: getDeviationSeverity(ws.riskScore, false),
        })),
        ...keyGaps.map((deviation) => ({
          project: projectName,
          policy,
          deviation,
          severity: getDeviationSeverity(ws.riskScore, true),
        })),
      ];
    })
    .sort((left, right) => POLICY_DEVIATION_SEVERITY_ORDER[left.severity] - POLICY_DEVIATION_SEVERITY_ORDER[right.severity]);

// Utility agents appear in the export in this fixed order, before policy deviations
const UTILITY_AGENT_TYPES = new Set<number>([
  AgentType.Summarization,
  AgentType.RedFlagClassifier,
  AgentType.GapDetector,
  // AgentType.ContradictionDetector,
  AgentType.CrossDocSynthesis,
]);

const UTILITY_AGENT_ORDER: AgentType[] = [
  AgentType.Summarization,
  AgentType.RedFlagClassifier,
  AgentType.GapDetector,
  // AgentType.ContradictionDetector,
  AgentType.CrossDocSynthesis,
];

const DOMAIN_AGENT_ORDER: AgentType[] = [
  AgentType.Cybersecurity,
  AgentType.InfrastructureCloud,
  AgentType.SoftwareEngineering,
  AgentType.DataAI,
  AgentType.Compliance,
  AgentType.VendorThirdParty,
  AgentType.IpLicensing,
  AgentType.ArchitectureScalability,
  AgentType.IncidentOps,
  AgentType.PeopleOrg,
  AgentType.FinancialDueDiligence,
];

const DEFAULT_SELECTED_AGENT_TYPES: AgentType[] = [
  AgentType.Summarization,
  AgentType.RedFlagClassifier,
  AgentType.GapDetector,
  // AgentType.ContradictionDetector,
];

const buildExportFindingSections = (
  workstreams: WorkstreamDto[],
  getAgentLabel: (agentType: number) => string,
): { utility: ExportFindingSection[]; domain: ExportFindingSection[] } => {
  const allSections = workstreams
    .map((ws) => {
      const findingTitles = safeParseJsonArray<WorkstreamFinding>(ws.findingsJson)
        .map((finding) => finding.title.trim())
        .filter((title) => title.length > 0);
      const redFlags = safeParseJsonArray<string>(ws.redFlags);
      const keyGaps = safeParseJsonArray<string>(ws.keyGaps);

      return {
        title: getAgentLabel(ws.agentType),
        agentType: ws.agentType as number,
        riskScore: ws.riskScore,
        findingTitles,
        redFlags,
        keyGaps,
      };
    })
    .filter((section) =>
      section.findingTitles.length > 0
      || section.redFlags.length > 0
      || section.keyGaps.length > 0
    );

  const utility = allSections
    .filter((s) => UTILITY_AGENT_TYPES.has(s.agentType))
    .sort((a, b) => UTILITY_AGENT_ORDER.indexOf(a.agentType) - UTILITY_AGENT_ORDER.indexOf(b.agentType));

  const domain = allSections
    .filter((s) => !UTILITY_AGENT_TYPES.has(s.agentType))
    .sort((a, b) => a.agentType - b.agentType);

  return { utility, domain };
};

const auditTrail = [
  { timestamp: "2024-01-15 14:32", user: "Anna Lindberg", action: "Approved", item: "Investment Committee Review", project: "Project Alpha" },
  { timestamp: "2024-01-15 11:20", user: "Erik Johansson", action: "Rejected", item: "Risk Assessment", project: "Project Beta" },
  { timestamp: "2024-01-14 16:45", user: "Maria Svensson", action: "Submitted", item: "Due Diligence Report", project: "Project Gamma" },
  { timestamp: "2024-01-14 09:15", user: "Johan Karlsson", action: "Updated", item: "Architecture Assessment", project: "Project Delta" },
];

const checklistItems = [
  { name: "IT Security Review", completed: 8, total: 15 },
];

const getRiskColor = (level: string) => {
  switch (level) {
    case "critical": return "bg-red-600";
    case "high": return "bg-orange-500";
    case "medium": return "bg-yellow-500";
    case "low": return "bg-green-500";
    default: return "bg-muted";
  }
};

const Reports = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportProjectId, setExportProjectId] = useState<string>("");
  const [selectedAgentTypes, setSelectedAgentTypes] = useState<Set<number>>(
    () => new Set<number>(DEFAULT_SELECTED_AGENT_TYPES),
  );
  const [includePolicyDeviations, setIncludePolicyDeviations] = useState(false);

  const toggleAgentType = useCallback((agentType: number, checked: boolean) => {
    setSelectedAgentTypes((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(agentType);
      } else {
        next.delete(agentType);
      }
      return next;
    });
  }, []);

  const toggleAgentGroup = useCallback((groupTypes: AgentType[], checked: boolean) => {
    setSelectedAgentTypes((current) => {
      const next = new Set(current);
      for (const agentType of groupTypes) {
        if (checked) {
          next.add(agentType);
        } else {
          next.delete(agentType);
        }
      }
      return next;
    });
  }, []);

  const isGroupFullySelected = useCallback(
    (groupTypes: AgentType[]): boolean =>
      groupTypes.length > 0 && groupTypes.every((agentType) => selectedAgentTypes.has(agentType)),
    [selectedAgentTypes],
  );

  const { data: myRoles = {} } = useMyProjectRolesQuery();

  const { data: projects = [] } = useProjectsQuery();

  useEffect(() => {
    if (projects.length > 0) {
      setExportProjectId((currentValue) => currentValue || String(projects[0].id));
    }
  }, [projects]);

  const getAgentLabel = useCallback((agentType: number): string => {
    const translationKey = AGENT_TYPE_TRANSLATION_KEYS[agentType];
    if (translationKey) {
      const translated = t(translationKey as Parameters<typeof t>[0]);
      if (translated !== translationKey) return translated;
    }
    return AGENT_TYPE_LABELS[agentType] ?? `Agent ${agentType}`;
  }, [t]);

  const workstreamQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: ["workstreams", "project", project.id],
      queryFn: () => apiClient.getWorkstreamsByProjectId(project.id),
      select: (data: WorkstreamDto[]) =>
        data.filter((ws) => DOMAIN_AGENT_TYPES.includes(ws.agentType)),
      enabled: project.id > 0,
      gcTime: 5 * 60 * 1000,
    })),
  });

  const allWorkstreams = useMemo(
    () => workstreamQueries.flatMap((query) => query.data ?? []),
    [workstreamQueries]
  );

  const projectById = useMemo(
    () => new Map<number, Project>(projects.map((project) => [project.id, project])),
    [projects]
  );

  const canExportSelectedProject = useMemo(() => {
    const selectedId = Number(exportProjectId);
    if (!selectedId) return false;
    const role = myRoles[selectedId];
    return role !== undefined && role <= RoleType.Editor;
  }, [exportProjectId, myRoles]);

  const riskCategories = useMemo(() => {
    const grouped = new Map<number, { low: number; medium: number; high: number; critical: number }>();
    for (const ws of allWorkstreams) {
      if (ws.riskScore === null || UTILITY_AGENT_TYPES.has(ws.agentType)) continue;
      const counters = grouped.get(ws.agentType) ?? { low: 0, medium: 0, high: 0, critical: 0 };
      if (ws.riskScore >= 9) counters.critical += 1;
      else if (ws.riskScore >= 7) counters.high += 1;
      else if (ws.riskScore >= 4) counters.medium += 1;
      else counters.low += 1;
      grouped.set(ws.agentType, counters);
    }

    return Array.from(grouped.entries()).map(([agentType, counters]) => ({
      category: getAgentLabel(agentType),
      ...counters,
    }));
  }, [allWorkstreams, getAgentLabel]);

  const projectComparison = useMemo(() => {
    const items = projects.map((project) => {
      const byProject = allWorkstreams.filter((ws) => ws.projectId === project.id);
      const riskScores = byProject.map((ws) => ws.riskScore).filter((score): score is number => score !== null);
      const riskScore = riskScores.length > 0
        ? Math.round(riskScores.reduce((sum, score) => sum + score, 0) / riskScores.length) * RISK_SCORE_SCALE
        : 0;
      const confidenceValues = byProject.map((ws) => ws.confidence).filter((c): c is number => c !== null);
      const maxConfidence = confidenceValues.length * 1.0;
      const confidenceScore = maxConfidence > 0
        ? Math.round((confidenceValues.reduce((sum, c) => sum + c, 0) / maxConfidence) * 100)
        : 0;
      const benchmarkScore = Math.round(((100 - riskScore) * confidenceScore) / 100);

      return {
        name: project.name,
        riskScore,
        confidenceScore,
        benchmarkScore,
        status: project.status,
      };
    });
    return items.sort((a, b) => b.benchmarkScore - a.benchmarkScore);
  }, [allWorkstreams, projects]);

  const getRiskDotClass = (riskScore: number): string => {
    if (riskScore >= 90) return "bg-red-600 text-white";
    if (riskScore >= 70) return "bg-orange-500 text-white";
    if (riskScore >= 40) return "bg-yellow-500 text-black";
    return "bg-green-500 text-white";
  };

  const getProjectStatusBadge = (status: number) => {
    switch (status) {
      case ProjectStatus.Active:    return <Badge className="bg-green-500 hover:bg-green-600 text-white">{t("active")}</Badge>;
      case ProjectStatus.OnHold:    return <Badge variant="secondary">{t("onHold")}</Badge>;
      case ProjectStatus.Cancelled: return <Badge variant="destructive">{t("cancelled")}</Badge>;
      case ProjectStatus.Done:      return <Badge variant="outline">{t("done")}</Badge>;
      default:                      return <Badge variant="outline">-</Badge>;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical": return <Badge variant="destructive">{t("critical")}</Badge>;
      case "high": return <Badge variant="destructive">{t("high")}</Badge>;
      case "medium": return <Badge className="bg-yellow-500 hover:bg-yellow-600">{t("medium")}</Badge>;
      case "low": return <Badge variant="secondary">{t("low")}</Badge>;
      default: return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "Approved": return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "Rejected": return <XCircle className="h-4 w-4 text-red-500" />;
      case "Submitted": return <FileText className="h-4 w-4 text-blue-500" />;
      case "Updated": return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return null;
    }
  };

  const getActionTranslation = (action: string) => {
    switch (action) {
      case "Approved": return t("approved");
      case "Rejected": return t("rejected");
      case "Submitted": return t("submitted");
      case "Updated": return t("updated");
      default: return action;
    }
  };

  const handleOpenExportDialog = () => setIsExportDialogOpen(true);

  const handleExportProjectReport = () => {
    const selectedProjectId = Number(exportProjectId);
    const selectedProject = projects.find((project) => project.id === selectedProjectId);

    if (!selectedProject) {
      return;
    }

    const projectWorkstreams = allWorkstreams.filter(
      (workstream) =>
        workstream.projectId === selectedProjectId
        && selectedAgentTypes.has(workstream.agentType),
    );
    const riskScores = projectWorkstreams
      .map((workstream) => workstream.riskScore)
      .filter((riskScore): riskScore is number => riskScore !== null);
    const averageRiskScore = riskScores.length > 0
      ? (riskScores.reduce((sum, score) => sum + score, 0) / riskScores.length).toFixed(1)
      : "0.0";
    const completedAssessments = projectWorkstreams.filter((workstream) => workstream.status === WorkstreamStatus.Completed).length;
    const redFlagsCount = projectWorkstreams.reduce((sum, workstream) => sum + safeParseJsonArray<string>(workstream.redFlags).length, 0);
    const keyGapsCount = projectWorkstreams.reduce((sum, workstream) => sum + safeParseJsonArray<string>(workstream.keyGaps).length, 0);
    const projectPolicyDeviations = buildPolicyDeviationEntries(
      projectWorkstreams,
      () => selectedProject.name,
      getAgentLabel,
    );
    const { utility: utilitySections, domain: domainSections } = buildExportFindingSections(projectWorkstreams, getAgentLabel);
    const completionRate = projectWorkstreams.length === 0
      ? "0%"
      : `${Math.round((completedAssessments / projectWorkstreams.length) * 100)}%`;

    const reportWindow = window.open("", "_blank");
    if (!reportWindow) {
      toast.error(`${t("exportBtn")} ${t("reports")}`);
      return;
    }
    reportWindow.opener = null;

    const policyDeviationMarkup = projectPolicyDeviations.length === 0
      ? `<p class="muted">${escapeHtml(t("noResults"))}</p>`
      : `
        <table class="data-table">
          <thead>
            <tr>
              <th>${escapeHtml(t("policyLabel"))}</th>
              <th>${escapeHtml(t("deviationLabel"))}</th>
              <th>${escapeHtml(t("severityLabel"))}</th>
            </tr>
          </thead>
          <tbody>
            ${projectPolicyDeviations.map((entry) => `
              <tr>
                <td>${escapeHtml(entry.policy)}</td>
                <td>${escapeHtml(entry.deviation)}</td>
                <td><span class="severity-chip ${getExportSeverityClass(entry.severity)}">${escapeHtml(t(entry.severity as Parameters<typeof t>[0]))}</span></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      `;

    const renderFindingSections = (sections: ExportFindingSection[]) =>
      sections.length === 0
        ? `<p class="muted">${escapeHtml(t("noFindings"))}</p>`
        : sections.map((section) => `
          <div class="finding-card">
            <h3>${escapeHtml(section.title)}</h3>
            <p class="muted"><strong>${escapeHtml(t("riskScore"))}:</strong> ${escapeHtml(section.riskScore === null ? "N/A" : `${section.riskScore}/${RISK_SCORE_SCALE}`)}</p>
            ${section.findingTitles.length > 0 ? `
              <div class="finding-block">
                <strong>${escapeHtml(t("findings"))}</strong>
                <ul>
                  ${section.findingTitles.map((title) => `<li>${escapeHtml(title)}</li>`).join("")}
                </ul>
              </div>
            ` : ""}
            ${section.redFlags.length > 0 ? `
              <div class="finding-block">
                <strong>${escapeHtml(t("redFlags"))}</strong>
                <ul>
                  ${section.redFlags.map((flag) => `<li>${escapeHtml(flag)}</li>`).join("")}
                </ul>
              </div>
            ` : ""}
            ${section.keyGaps.length > 0 ? `
              <div class="finding-block">
                <strong>${escapeHtml(t("keyGaps"))}</strong>
                <ul>
                  ${section.keyGaps.map((gap) => `<li>${escapeHtml(gap)}</li>`).join("")}
                </ul>
              </div>
            ` : ""}
          </div>
        `).join("");

    const utilityFindingsMarkup = renderFindingSections(utilitySections);
    const domainFindingsMarkup = renderFindingSections(domainSections);

    const reportHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(`${selectedProject.name} - ${t("reports")}`)}</title>
          <style>
            body { font-family: "Segoe UI", Arial, sans-serif; color: #0f172a; margin: 0; line-height: 1.5; background: #f8fafc; }
            h1, h2, h3 { margin-bottom: 8px; }
            h1 { font-size: 28px; margin: 0 0 8px; color: #f8fafc; }
            h2 { font-size: 20px; margin-top: 28px; border-bottom: 1px solid #dbeafe; padding-bottom: 6px; color: #1d4ed8; }
            h3 { font-size: 16px; margin-top: 18px; color: #1e293b; }
            .muted { color: #64748b; }
            .container { max-width: 1000px; margin: 28px auto; background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08); padding: 28px; }
            .header { background: linear-gradient(135deg, #1d4ed8, #0ea5e9); color: #f8fafc; border-radius: 12px; padding: 18px 20px; margin-bottom: 20px; }
            .meta { margin-bottom: 20px; color: #e0f2fe; }
            .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
            .summary-card { border: 1px solid #dbeafe; border-radius: 10px; padding: 12px; background: #f8fbff; }
            .summary-value { font-size: 22px; font-weight: 700; margin-top: 6px; color: #1d4ed8; }
            .finding-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-top: 16px; background: #f8fafc; }
            .finding-block { margin-top: 12px; }
            .data-table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            .data-table th, .data-table td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; vertical-align: top; }
            .data-table th { background: #eff6ff; color: #1e3a8a; }
            .severity-chip { display: inline-block; border-radius: 9999px; padding: 2px 10px; font-weight: 600; font-size: 12px; text-transform: capitalize; }
            .severity-critical { background: #dc2626; color: #ffffff; }
            .severity-high { background: #f97316; color: #ffffff; }
            .severity-medium { background: #eab308; color: #111827; }
            .severity-low { background: #22c55e; color: #ffffff; }
            p { margin: 8px 0; }
            ul { margin-top: 6px; }
           </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>${escapeHtml(selectedProject.name)}</h1>
              <p>${escapeHtml(t("reportsSubtitle"))}</p>
              <p class="meta"><strong>${escapeHtml(t("timestamp"))}:</strong> ${escapeHtml(new Date().toLocaleString())}</p>
            </div>

            <h2>${escapeHtml(t("riskCompliance"))}</h2>
            <div class="summary-grid">
              <div class="summary-card">
                <div>${escapeHtml(t("riskScore"))}</div>
                <div class="summary-value">${escapeHtml(averageRiskScore)}</div>
              </div>
              <div class="summary-card">
                <div>${escapeHtml(t("completionRate"))}</div>
                <div class="summary-value">${escapeHtml(completionRate)}</div>
              </div>
              <div class="summary-card">
                <div>${escapeHtml(t("redFlags"))}</div>
                <div class="summary-value">${escapeHtml(String(redFlagsCount))}</div>
              </div>
              <div class="summary-card">
                <div>${escapeHtml(t("keyGaps"))}</div>
                <div class="summary-value">${escapeHtml(String(keyGapsCount))}</div>
              </div>
            </div>

            ${UTILITY_AGENT_ORDER.some((agentType) => selectedAgentTypes.has(agentType)) ? `
            <h2>${escapeHtml(t("crossAnalysisFindings"))}</h2>
            ${utilityFindingsMarkup}
            ` : ""}

            ${includePolicyDeviations ? `
            <h2>${escapeHtml(t("policyDeviations"))}</h2>
            ${policyDeviationMarkup}
            ` : ""}

            ${DOMAIN_AGENT_ORDER.some((agentType) => selectedAgentTypes.has(agentType)) ? `
            <h2>${escapeHtml(t("findings"))}</h2>
            ${domainFindingsMarkup}
            ` : ""}
          </div>
        </body>
      </html>
    `;

    reportWindow.document.open();
    reportWindow.document.write(reportHtml);
    reportWindow.document.close();
    reportWindow.focus();
    window.setTimeout(() => {
      if (!reportWindow.closed) {
        reportWindow.print();
      }
    }, PRINT_DELAY_MS);
    setIsExportDialogOpen(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t("reports")}</h1>
            <p className="text-muted-foreground mt-1">{t("reportsSubtitle")}</p>
          </div>
          <Button className="w-full sm:w-auto" onClick={handleOpenExportDialog} disabled={projects.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            {t("exportBtn")}
          </Button>
        </div>

        <Tabs defaultValue="risk" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 w-full">
            <TabsTrigger value="risk" className="flex-1 min-w-fit">{t("riskCompliance")}</TabsTrigger>
            <TabsTrigger value="portfolio" className="flex-1 min-w-fit">{t("portfolioAnalysis")}</TabsTrigger>
          </TabsList>

          {/* Risk & Compliance Tab */}
          <TabsContent value="risk" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Risk Heatmap */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    {t("riskHeatmapTitle")}
                  </CardTitle>
                  <CardDescription>{t("riskDistribution")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span> {t("low")}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> {t("medium")}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500"></span> {t("high")}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-600"></span> {t("critical")}</span>
                    </div>
                    {riskCategories.map((cat) => (
                      <div key={cat.category} className="space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">{cat.category}</span>
                        </div>
                        <div className="flex gap-1 h-6">
                          {cat.low > 0 && <div className={`${getRiskColor("low")} rounded flex-1`} style={{ flex: cat.low }}></div>}
                          {cat.medium > 0 && <div className={`${getRiskColor("medium")} rounded flex-1`} style={{ flex: cat.medium }}></div>}
                          {cat.high > 0 && <div className={`${getRiskColor("high")} rounded flex-1`} style={{ flex: cat.high }}></div>}
                          {cat.critical > 0 && <div className={`${getRiskColor("critical")} rounded flex-1`} style={{ flex: cat.critical }}></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

          </TabsContent>

          {/* Portfolio Analysis Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Project Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("projectVsProject")}</CardTitle>
                  <CardDescription>{t("compareProjects")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("projectLabel")}</TableHead>
                        <TableHead className="text-center">{t("riskScore")}</TableHead>
                        <TableHead className="text-center">{t("confidenceScore")}</TableHead>
                        {/* <TableHead className="text-center">{t("benchmarkScore")}</TableHead> */}
                        <TableHead>{t("status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectComparison.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t("noResults")}</TableCell>
                        </TableRow>
                      ) : (
                        projectComparison.map((project) => (
                          <TableRow key={project.name}>
                            <TableCell className="font-medium">{project.name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={project.riskScore >= 60 ? "destructive" : project.riskScore >= 40 ? "secondary" : "outline"}>
                                {project.riskScore}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-primary/10">
                                {project.confidenceScore}
                              </Badge>
                            </TableCell>
                            {/* <TableCell className="text-center">
                              <Badge variant={project.benchmarkScore >= 60 ? "default" : project.benchmarkScore >= 30 ? "secondary" : "outline"}>
                                {project.benchmarkScore}
                              </Badge>
                            </TableCell> */}
                            <TableCell>
                              {getProjectStatusBadge(project.status)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Risk vs Confidence Matrix */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("riskVsConfidence")}</CardTitle>
                  <CardDescription>{t("projectPositioning")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative h-64 border rounded-lg p-4">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 text-xs text-muted-foreground">
                      {t("risk")} →
                    </div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs text-muted-foreground">
                      {t("confidenceScore")} →
                    </div>
                    {/* Grid lines */}
                    <div className="absolute inset-4 border-l border-b border-dashed border-muted-foreground/30">
                      <div className="absolute left-1/2 top-0 bottom-0 border-l border-dashed border-muted-foreground/30"></div>
                      <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-muted-foreground/30"></div>
                    </div>
                    {/* Project dots */}
                    {projectComparison.map((project) => (
                      <Tooltip key={project.name}>
                        <TooltipTrigger asChild>
                          <div
                            className={`absolute w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer hover:scale-110 transition-transform ${getRiskDotClass(project.riskScore)}`}
                            style={{
                              left: `${(project.confidenceScore / 100) * 80 + 10}%`,
                              bottom: `${(project.riskScore / 100) * 80 + 10}%`,
                            }}
                          >
                            {project.name.charAt(0).toUpperCase()}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>{project.name}</TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {projectComparison.map((project) => (
                      <Badge key={project.name} variant="outline" className={`text-xs ${getRiskDotClass(project.riskScore)}`}>
                        {project.name.charAt(0).toUpperCase()} = {project.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

          </TabsContent>

        </Tabs>
      </div>
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("exportBtn")} {t("reports")}</DialogTitle>
            <DialogDescription>{t("reportsSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("selectProject")}</p>
            <Select value={exportProjectId} onValueChange={setExportProjectId}>
              <SelectTrigger>
                <SelectValue placeholder={t("selectProject")} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-4">
            <p className="text-sm font-medium">{t("exportSections")}</p>
            {([
              { labelKey: "crossAnalysisFindings" as const, types: UTILITY_AGENT_ORDER },
              { labelKey: "findings" as const, types: DOMAIN_AGENT_ORDER },
            ]).map((group) => (
              <div key={group.labelKey} className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">{t(group.labelKey)}</p>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={isGroupFullySelected(group.types)}
                      onCheckedChange={(checked) => toggleAgentGroup(group.types, checked === true)}
                    />
                    {t("selectAll")}
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {group.types.map((agentType) => {
                    const checkboxId = `export-section-${agentType}`;
                    return (
                      <div key={agentType} className="flex items-center gap-2">
                        <Checkbox
                          id={checkboxId}
                          checked={selectedAgentTypes.has(agentType)}
                          onCheckedChange={(checked) => toggleAgentType(agentType, checked === true)}
                        />
                        <Label htmlFor={checkboxId} className="text-sm font-normal">
                          {getAgentLabel(agentType)}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="export-section-policy-deviations"
                  checked={includePolicyDeviations}
                  onCheckedChange={(checked) => setIncludePolicyDeviations(checked === true)}
                />
                <Label htmlFor="export-section-policy-deviations" className="text-xs font-semibold uppercase text-muted-foreground">
                  {t("policyDeviations")}
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button type="button" onClick={handleExportProjectReport} disabled={!exportProjectId || !canExportSelectedProject || selectedAgentTypes.size === 0}>
              {t("exportBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Reports;
